import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { NotificationActions } from './notificationSlice';

var urlPrefix = '';

switch(process.env.ENVIRONMENT_SETTINGS) {
case 'local':
    urlPrefix = 'http://localhost:8000';
    break;
case 'staging':
    urlPrefix = 'https://staging.docsihealth.com';
    break;
case 'demo':
    urlPrefix = 'https://demo.docsihealth.com';
    break;
case 'production':
    urlPrefix = 'https://api.docsihealth.com';
    break;
}

const buildBaseQuery = () => {
    const query = fetchBaseQuery({
        baseUrl: `${urlPrefix}/api`,
        prepareHeaders: (headers) => {
            const token = localStorage.getItem('reviewToken');
            if (token) {
                headers.set('authorization', `Bearer ${token}`)
            }
            headers.set('accept', 'application/json');
            return headers;
        },
        credentials: 'include'
    });
    return async (args, api, extraOptions) => {
        const { error, data } = await query(args, api, extraOptions);
        if (error) {
            if (error.status === 401) {
                localStorage.removeItem('reviewToken');
                window.location.href = '/login';
            }
            return { error: { status: error.status, data: error.data } };
        }
        return { data };
    }
}

export const staffReviewSlice = createApi({
    reducerPath: 'staffReviewApi',
    baseQuery: buildBaseQuery(),
    tagTypes: ['CardsForReview', 'CompletedReviews', 'UserStatistics'],
    endpoints: (builder) => ({
        getCardsForReview: builder.query({
            query: (userId) => `/users/${userId}/staffReviews`,
            providesTags: ['CardsForReview']
        }),
        getReview: builder.query({
            query: (draftId) => `/drafts/${draftId}/staffReview`,
            providesTags: (result = [], error, arg) => [
                { type: 'CardsForReview', id: arg },
            ],
            keepUnusedDataFor: 0,
        }),
        updateReview: builder.mutation({
            query: ({ review, draftId, recommendation, newStatus, notes }) => ({
                url: `/staffReviews/${review.id}`,
                method: 'PATCH',
                body: {
                    status: newStatus,
                    notes: notes,
                }
            }),
            async onQueryStarted({ review, draftId, recommendation, newStatus, notes }, { dispatch, queryFulfilled, queryRejected }) {
                try {
                    const { data: updatedReview } = await queryFulfilled;
                    // Update the individual review cache
                    dispatch(staffReviewSlice.util.updateQueryData('getReview', draftId, draft => {
                        let existingRecIndex = draft.recommendations[recommendation.item.category].find((rec) => rec.id === recommendation.id);
                        if (existingRecIndex) {
                            Object.assign(existingRecIndex.clinical_review, updatedReview);
                        }
                    }));
                    dispatch(NotificationActions.addSuccessNotification({
                        message: 'Review updated successfully.'
                    }));
                } catch (error) {
                    if (error.status === 403) {
                        dispatch(NotificationActions.addErrorNotification({
                            message: 'You do not currently have permission to update this review.'
                        }));
                    } else {
                        dispatch(NotificationActions.addErrorNotification({
                            message: 'Could not submit review at this time. Please try again later.'
                        }));
                    }
                }
            }
        }),
        updateDraft: builder.mutation({
            query: ({ draftId, status }) => ({
                url: `/drafts/${draftId}`,
                method: 'PATCH',
                body: {
                    status: status,
                    staff_review_completion_time: new Date().toISOString()
                }
            }),
            async onQueryStarted({ draftId, status }, { dispatch, queryFulfilled, queryRejected }) {
                try {
                    const { data: updatedDraft } = await queryFulfilled;
                    dispatch(staffReviewSlice.util.updateQueryData('getReview', draftId.toString(), draft => {
                        Object.assign(draft, updatedDraft);
                    }));
                    dispatch(NotificationActions.addSuccessNotification({
                        message: 'Review changes submitted successfully.'
                    }));
                } catch (error) {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Could not submit changes at this time. Please try again later.'
                    }));
                }
            }
        }),
        getCompletedReviews: builder.query({
            query: (userId) => `/users/${userId}/completedStaffReviews`,
            providesTags: (result = [], error, arg) => [
                ...result.map(({ id }) => ({ type: 'CompletedReviews', id }))
            ]
        }),
        getCurrentUser: builder.query({
            query: () => '/user'
        }),
        createDraftCheckoutLog: builder.mutation({
            query: ({ draftId, action, reason }) => ({
                url: `/drafts/${draftId}/checkoutLogs`,
                method: 'POST',
                body: {
                    action: action,
                    reason: reason,
                }
            })
        }),
        logUserAction: builder.mutation({
            query: ({ action, data }) => ({
                url: '/userActions',
                method: 'POST',
                body: {
                    action: action,
                    data: data
                }
            }),
        }),
        getUserStatistics: builder.query({
            query: (userId) => `/users/${userId}/staffReviewStatistics`,
            providesTags: ['UserStatistics']
        }),
    })
});

export const {
    useGetCardsForReviewQuery,
    useGetReviewQuery,
    useUpdateReviewMutation,
    useUpdateDraftMutation,
    useGetCompletedReviewsQuery,
    useGetCurrentUserQuery,
    useCreateDraftCheckoutLogMutation,
    useLogUserActionMutation,
    useGetUserStatisticsQuery,
} = staffReviewSlice;
